import { QRDotStyle, QRCornerSquareStyle, QRCornerDotStyle } from '../types/qr.types';

export function getQrTargetUrl(qrCode: { short_code: string; custom_domain?: string }): string {
  const baseUrl = qrCode.custom_domain
    ? `https://${qrCode.custom_domain}`
    : window.location.origin;
  return `${baseUrl}/r/${qrCode.short_code}`;
}

export const DOT_STYLE_OPTIONS: { value: QRDotStyle; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'dots', label: 'Dots' },
  { value: 'rounded', label: 'Rounded' },
  { value: 'classy', label: 'Classy' },
  { value: 'classy-rounded', label: 'Classy Rounded' },
  { value: 'extra-rounded', label: 'Extra Rounded' },
];

export const CORNER_SQUARE_STYLE_OPTIONS: { value: QRCornerSquareStyle; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Circle' },
  { value: 'extra-rounded', label: 'Rounded' },
];

export const CORNER_DOT_STYLE_OPTIONS: { value: QRCornerDotStyle; label: string }[] = [
  { value: 'square', label: 'Square' },
  { value: 'dot', label: 'Dot' },
];

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (n: number) => Math.round(f(n) * 255).toString(16).padStart(2, '0');
  return `#${toHex(0)}${toHex(8)}${toHex(4)}`.toUpperCase();
}

function pickRandom<T>(options: readonly T[]): T {
  return options[Math.floor(Math.random() * options.length)];
}

export interface RandomQrAppearance {
  background_color: string;
  foreground_color: string;
  dots_type: QRDotStyle;
  corners_square_type: QRCornerSquareStyle;
  corners_dot_type: QRCornerDotStyle;
}

/**
 * Generates a random dot/corner style combo plus a complementary color pair.
 * Foreground stays dark and background stays very light regardless of hue,
 * so contrast (and QR scannability) is guaranteed even though the hues are randomized.
 */
export function randomQrAppearance(): RandomQrAppearance {
  const hue = Math.floor(Math.random() * 360);
  const complementaryHue = (hue + 180) % 360;

  return {
    foreground_color: hslToHex(hue, 55 + Math.random() * 30, 15 + Math.random() * 15),
    background_color: hslToHex(complementaryHue, 30 + Math.random() * 25, 92 + Math.random() * 6),
    dots_type: pickRandom(DOT_STYLE_OPTIONS).value,
    corners_square_type: pickRandom(CORNER_SQUARE_STYLE_OPTIONS).value,
    corners_dot_type: pickRandom(CORNER_DOT_STYLE_OPTIONS).value,
  };
}
